import React, { useCallback, useContext, useEffect, useState } from "react";
import ListPagination from "components/ListPagination";
import { Row, Spinner } from "react-bootstrap";
import ImageGallery from "components/ImageGallery";
import CaseApi from "api/case-api";
import { CaseImagesContext } from "pages/cases/view/case-images/CaseImages";
import { getVideoDisplayBaseName, addCounterToVideoName } from "helpers/utils";

const caseApi = new CaseApi();

const Videos = () => {
    const [totalPages, setTotalPages] = useState(0);
    const [gallery, setGallery] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const {
        captureDate,
        caseInfo,
        locationInfo,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        onGoToImage,
        mediaFileRenamed
    } = useContext(CaseImagesContext);

    const getVideos = async () => {
        try {
            setIsLoading(true);
            const offset = (currentPage - 1) * pageSize;
            const { images, count } = await caseApi.getLocationImages(
                caseInfo.id,
                locationInfo.id,
                {
                    capture_date: captureDate,
                    limit: pageSize,
                    offset: offset,
                    media_type: "VIDEO"
                }
            );
            // Number duplicate base names (2), (3)... in creation (id) order,
            // so "which one is (2)" never depends on display sort
            const numberingOrder = [...images].sort((a: any, b: any) => a.id - b.id);
            const nameCounts: { [base: string]: number } = {};
            const displayNames: { [id: number]: string } = {};
            for (const media of numberingOrder) {
                const base = getVideoDisplayBaseName(media.filename);
                const n = (nameCounts[base] = (nameCounts[base] || 0) + 1);
                displayNames[media.id] = n > 1 ? addCounterToVideoName(base, n) : base;
            }
            setGallery(images.map((media: any) => ({ ...media, display_name: displayNames[media.id] })));
            setTotalPages(Math.ceil(count / pageSize));
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    // initialize things
    useEffect(() => {
        setCurrentPage(1);
        setPageSize(240);
    }, []);

    // Fetch videos only
    useEffect(() => {
        getVideos();
    }, [currentPage, pageSize, captureDate, caseInfo.id, locationInfo.id, mediaFileRenamed]);

    const onSetPageSize = useCallback((size: number) => {
        setPageSize(size);
    }, []);

    const onChangePage = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    return (
        <React.Fragment>
            <ListPagination
                className="pagination-top"
                pageSize={pageSize}
                currentPage={currentPage}
                totalPages={totalPages}
                onSetPageSize={onSetPageSize}
                onChangePage={onChangePage}
                onGoToImage={onGoToImage}
            />

            <Row className="filterable-content position-relative">
                {isLoading && (
                    <div className="d-flex justify-content-center">
                        <Spinner className="text-primary m-2" />
                    </div>
                )}
                {!isLoading && gallery.length === 0 && (
                    <span className="text-muted">No videos found</span>
                )}
                <ImageGallery name="Video" images={gallery} onVideoDeleted={getVideos} />
            </Row>

            <ListPagination
                className="pagination-bottom"
                pageSize={pageSize}
                currentPage={currentPage}
                totalPages={totalPages}
                onSetPageSize={onSetPageSize}
                onChangePage={onChangePage}
                onGoToImage={onGoToImage}
            />
        </React.Fragment>
    );
};

export default React.memo(Videos);